import React, { useEffect, useState, useCallback } from 'react';
import { DateTimeInput } from '@/components/ui/date-input';
import { format } from 'date-fns';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Save, Send, Users, Mail, MessageCircle, MessageSquare, Calendar, Zap, CheckCircle, XCircle, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const BroadcastWizardPage = () => {
  const { broadcastId } = useParams();
  const navigate = useNavigate();
  const isNew = !broadcastId || broadcastId === 'new';
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Data
  const [name, setName] = useState('');
  const [channels, setChannels] = useState(['email']);
  const [audienceType, setAudienceType] = useState('segment');
  const [segmentId, setSegmentId] = useState('');
  const [staticListId, setStaticListId] = useState('');
  const [manualRecipients, setManualRecipients] = useState('');
  const [emailAccountId, setEmailAccountId] = useState('');
  const [emailTemplateId, setEmailTemplateId] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBodyHtml, setEmailBodyHtml] = useState('');
  const [waTemplateName, setWaTemplateName] = useState('');
  const [waTemplateLang, setWaTemplateLang] = useState('en');
  const [fallbackMode, setFallbackMode] = useState('none');
  const [scheduleType, setScheduleType] = useState('now');
  const [scheduledAt, setScheduledAt] = useState('');
  const [throttlePerMinute, setThrottlePerMinute] = useState('');
  const [sendWindowEnabled, setSendWindowEnabled] = useState(false);
  const [sendWindowStart, setSendWindowStart] = useState(9);
  const [sendWindowEnd, setSendWindowEnd] = useState(21);
  const [sendWindowTz, setSendWindowTz] = useState('UTC');

  // Options
  const [segments, setSegments] = useState([]);
  const [staticLists, setStaticLists] = useState([]);
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [waTemplates, setWaTemplates] = useState([]);
  const [audiencePreview, setAudiencePreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [broadcastStatus, setBroadcastStatus] = useState('draft');

  const toggleChannel = (ch) => {
    setChannels(prev => prev.includes(ch) ? prev.filter(c => c !== ch) : [...prev, ch]);
  };

  const loadInitial = useCallback(async () => {
    try {
      const [segs, lists, accs, tpls, waTpls] = await Promise.all([
        axios.get(`${API}/segments`, { headers }).catch(() => ({ data: { segments: [] } })),
        axios.get(`${API}/segments/static-lists/all`, { headers }).catch(() => ({ data: { lists: [] } })),
        axios.get(`${API}/email/accounts`, { headers }).catch(() => ({ data: { accounts: [] } })),
        axios.get(`${API}/email-templates`, { headers }).catch(() => ({ data: { templates: [] } })),
        axios.get(`${API}/whatsapp/templates`, { headers }).catch(() => ({ data: { templates: [] } })),
      ]);
      setSegments(segs.data.segments || []);
      setStaticLists(lists.data.lists || []);
      const accsList = accs.data.accounts || accs.data || [];
      setEmailAccounts(Array.isArray(accsList) ? accsList : []);
      setEmailTemplates(tpls.data.templates || []);
      setWaTemplates((waTpls.data.templates || []).filter(t => (t.status || '').toUpperCase() === 'APPROVED'));
      if (Array.isArray(accsList) && accsList[0]?.id) setEmailAccountId(accsList[0].id);

      if (!isNew) {
        const r = await axios.get(`${API}/broadcasts/${broadcastId}`, { headers });
        const b = r.data;
        setName(b.name || '');
        setChannels(b.channels || ['email']);
        setAudienceType(b.audience_type || 'segment');
        setSegmentId(b.segment_id || '');
        setStaticListId(b.static_list_id || '');
        setManualRecipients((b.manual_recipients || []).map(r => r.email || r.phone).join('\n'));
        const c = b.content || {};
        setEmailAccountId(c.email_from_account_id || '');
        setEmailTemplateId(c.email_template_id || '');
        setEmailSubject(c.email_subject || '');
        setEmailBodyHtml(c.email_body_html || '');
        setWaTemplateName(c.whatsapp_template_name || '');
        setWaTemplateLang(c.whatsapp_template_language || 'en');
        setFallbackMode(b.fallback_mode || 'none');
        setScheduleType(b.schedule_type || 'now');
        setScheduledAt(b.scheduled_at || '');
        setThrottlePerMinute(b.throttle_per_minute ? String(b.throttle_per_minute) : '');
        const sw = b.send_window || {};
        setSendWindowEnabled(!!sw.enabled);
        setSendWindowStart(typeof sw.start_hour === 'number' ? sw.start_hour : 9);
        setSendWindowEnd(typeof sw.end_hour === 'number' ? sw.end_hour : 21);
        setSendWindowTz(sw.timezone || 'UTC');
        setBroadcastStatus(b.status || 'draft');
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to load');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [broadcastId, isNew]);

  useEffect(() => { loadInitial(); }, [loadInitial]);

  // Auto-preview audience when inputs change (debounced)
  useEffect(() => {
    if (!channels.length) { setAudiencePreview(null); return; }
    if (audienceType === 'segment' && !segmentId) return;
    if (audienceType === 'static_list' && !staticListId) return;
    const t = setTimeout(async () => {
      setPreviewing(true);
      try {
        const manual = manualRecipients.split(/[\n,;]/).map(s => s.trim()).filter(Boolean).map(s =>
          s.includes('@') ? { email: s } : { phone: s }
        );
        const r = await axios.post(`${API}/broadcasts/preview-audience`, {
          channels,
          audience_type: audienceType,
          segment_id: segmentId || null,
          static_list_id: staticListId || null,
          manual_recipients: manual,
        }, { headers });
        setAudiencePreview(r.data);
      } catch (e) { /* silent */ }
      finally { setPreviewing(false); }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channels.join(','), audienceType, segmentId, staticListId, manualRecipients]);

  // Auto-fill subject/body from selected template
  useEffect(() => {
    if (!emailTemplateId) return;
    const tpl = emailTemplates.find(t => t.id === emailTemplateId);
    if (tpl) {
      if (!emailSubject) setEmailSubject(tpl.subject || '');
    }
  }, [emailTemplateId, emailTemplates, emailSubject]);

  const buildContent = () => ({
    email_from_account_id: emailAccountId || null,
    email_template_id: emailTemplateId || null,
    email_subject: emailSubject,
    email_body_html: emailBodyHtml,
    whatsapp_template_name: waTemplateName || null,
    whatsapp_template_language: waTemplateLang,
    whatsapp_template_variables: [],
  });

  const buildManual = () =>
    manualRecipients.split(/[\n,;]/).map(s => s.trim()).filter(Boolean).map(s =>
      s.includes('@') ? { email: s } : { phone: s }
    );

  const save = async () => {
    if (!name.trim()) { toast.error('Name required'); return; }
    if (!channels.length) { toast.error('Select at least one channel'); return; }
    if (scheduleType === 'scheduled' && !scheduledAt) { toast.error('Pick a schedule date & time'); return; }
    setSaving(true);
    try {
      const body = {
        name, channels,
        audience_type: audienceType,
        segment_id: segmentId || null,
        static_list_id: staticListId || null,
        manual_recipients: buildManual(),
        content: buildContent(),
        fallback_mode: fallbackMode,
        schedule_type: scheduleType,
        scheduled_at: scheduleType === 'scheduled' ? scheduledAt : null,
        throttle_per_minute: throttlePerMinute ? parseInt(throttlePerMinute, 10) : null,
        send_window: {
          enabled: sendWindowEnabled,
          start_hour: parseInt(sendWindowStart, 10) || 0,
          end_hour: parseInt(sendWindowEnd, 10) || 0,
          timezone: sendWindowTz || 'UTC',
        },
      };
      if (isNew) {
        const r = await axios.post(`${API}/broadcasts`, body, { headers });
        toast.success('Broadcast saved');
        navigate(`/marketing/broadcasts/${r.data.broadcast.id}`, { replace: true });
      } else {
        await axios.put(`${API}/broadcasts/${broadcastId}`, body, { headers });
        toast.success('Saved');
      }
    } catch (e) {
      toast.error('Save failed: ' + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const launch = async () => {
    if (isNew) { toast.error('Save first'); return; }
    if (!window.confirm(`Launch broadcast to ${audiencePreview?.total_unique || 0} recipients?`)) return;
    setLaunching(true);
    try {
      // Save latest first
      await save();
      await axios.post(`${API}/broadcasts/${broadcastId}/launch`, {}, { headers });
      toast.success('Broadcast launching in background');
      setBroadcastStatus('sending');
      setTimeout(() => navigate(`/marketing/broadcasts/${broadcastId}`), 500);
    } catch (e) {
      toast.error('Launch failed: ' + (e?.response?.data?.detail || e.message));
    } finally { setLaunching(false); }
  };

  const readOnly = broadcastStatus === 'sending' || broadcastStatus === 'completed';

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="broadcast-wizard">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/marketing/broadcasts')}>
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-xl md:text-2xl font-semibold text-white">{isNew ? 'New Broadcast' : 'Edit Broadcast'}</h1>
            <p className="text-xs text-gray-500">Multi-channel one-off campaign with per-broadcast fallback.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!readOnly && (
            <>
              <Button variant="outline" onClick={save} disabled={saving} data-testid="save-broadcast">
                <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save Draft'}
              </Button>
              <Button onClick={launch} disabled={launching || isNew || !audiencePreview?.total_unique} className="bg-gradient-to-r from-pink-500 to-orange-500 hover:opacity-90" data-testid="launch-broadcast">
                <Send className="w-4 h-4 mr-1" /> {launching ? 'Launching…' : (scheduleType === 'scheduled' ? 'Schedule' : 'Launch Now')}
              </Button>
            </>
          )}
          {readOnly && <Badge className="bg-yellow-500/20 text-yellow-300 border-yellow-500/30">{broadcastStatus}</Badge>}
          {!isNew && (broadcastStatus === 'completed' || broadcastStatus === 'sending') && (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  const url = `${API}/broadcasts/${broadcastId}/recipients.csv?__auth=${encodeURIComponent(token)}`;
                  // Use fetch to honor Authorization header, then trigger download via blob
                  fetch(`${API}/broadcasts/${broadcastId}/recipients.csv`, { headers })
                    .then(r => r.blob())
                    .then(blob => {
                      const a = document.createElement('a');
                      a.href = URL.createObjectURL(blob);
                      a.download = `broadcast_${broadcastId}.csv`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                    })
                    .catch(() => toast.error('Export failed'));
                }}
                data-testid="export-broadcast-csv"
              >
                Export CSV
              </Button>
              <Button
                variant="outline"
                onClick={async () => {
                  if (!window.confirm('Retry all failed recipients?')) return;
                  try {
                    const r = await axios.post(`${API}/broadcasts/${broadcastId}/retry-failed`, {}, { headers });
                    toast.success(r.data?.message || 'Retry queued');
                  } catch (e) {
                    toast.error('Retry failed: ' + (e?.response?.data?.detail || e.message));
                  }
                }}
                data-testid="retry-failed-broadcast"
              >
                Retry Failed
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* Name & Channels */}
          <section className="p-4 bg-wt-surface border border-wt-border rounded-lg">
            <div className="font-semibold text-white text-sm mb-3">1. Name & Channels</div>
            <Label className="text-white">Broadcast Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} disabled={readOnly} className="bg-wt-bg mt-1" placeholder="e.g., Diwali Sale 2026" data-testid="broadcast-name" />
            <div className="mt-3">
              <Label className="text-white">Channels {channels.length > 1 && <span className="text-xs text-gray-500">(order = fallback priority)</span>}</Label>
              <div className="flex gap-2 mt-2 flex-wrap">
                {[{ id: 'email', label: 'Email', Icon: Mail }, { id: 'whatsapp', label: 'WhatsApp', Icon: MessageCircle }, { id: 'sms', label: 'SMS (coming soon)', Icon: MessageSquare, disabled: true }].map(ch => (
                  <button
                    key={ch.id}
                    onClick={() => !ch.disabled && !readOnly && toggleChannel(ch.id)}
                    disabled={ch.disabled || readOnly}
                    data-testid={`channel-${ch.id}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition ${
                      channels.includes(ch.id)
                        ? 'bg-pink-500/20 border-pink-500/50 text-white'
                        : 'bg-wt-bg border-wt-border text-gray-400 hover:border-wt-gold'
                    } ${ch.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <ch.Icon className="w-4 h-4" />
                    <span className="text-sm">{ch.label}</span>
                    {channels.includes(ch.id) && <CheckCircle className="w-3 h-3" />}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Audience */}
          <section className="p-4 bg-wt-surface border border-wt-border rounded-lg">
            <div className="font-semibold text-white text-sm mb-3">2. Audience</div>
            <Select value={audienceType} onValueChange={setAudienceType} disabled={readOnly}>
              <SelectTrigger className="bg-wt-bg" data-testid="audience-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All customers</SelectItem>
                <SelectItem value="segment">Live Segment</SelectItem>
                <SelectItem value="static_list">Static List</SelectItem>
                <SelectItem value="manual">Manual (paste emails / phones)</SelectItem>
              </SelectContent>
            </Select>
            {audienceType === 'segment' && (
              <Select value={segmentId} onValueChange={setSegmentId} disabled={readOnly}>
                <SelectTrigger className="bg-wt-bg mt-2" data-testid="segment-select"><SelectValue placeholder="Choose a segment…" /></SelectTrigger>
                <SelectContent>
                  {segments.length === 0 ? <div className="px-3 py-2 text-sm text-gray-500">No segments. Create one under Customers → Segments.</div> :
                    segments.map(s => <SelectItem key={s.id} value={s.id}>{s.name} · {(s.member_count ?? '—').toLocaleString?.() ?? '—'}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {audienceType === 'static_list' && (
              <Select value={staticListId} onValueChange={setStaticListId} disabled={readOnly}>
                <SelectTrigger className="bg-wt-bg mt-2"><SelectValue placeholder="Choose a list…" /></SelectTrigger>
                <SelectContent>
                  {staticLists.length === 0 ? <div className="px-3 py-2 text-sm text-gray-500">No lists yet.</div> :
                    staticLists.map(l => <SelectItem key={l.id} value={l.id}>{l.name} · {l.member_count || 0}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {audienceType === 'manual' && (
              <Textarea value={manualRecipients} onChange={(e) => setManualRecipients(e.target.value)} disabled={readOnly} rows={5} placeholder="jane@example.com&#10;+971501234567&#10;bob@example.com" className="bg-wt-bg mt-2" data-testid="manual-recipients" />
            )}
          </section>

          {/* Compose */}
          <section className="p-4 bg-wt-surface border border-wt-border rounded-lg">
            <div className="font-semibold text-white text-sm mb-3">3. Compose</div>
            <Tabs defaultValue={channels[0] || 'email'}>
              <TabsList className="bg-wt-paper">
                {channels.includes('email') && <TabsTrigger value="email"><Mail className="w-4 h-4 mr-1" /> Email</TabsTrigger>}
                {channels.includes('whatsapp') && <TabsTrigger value="whatsapp"><MessageCircle className="w-4 h-4 mr-1" /> WhatsApp</TabsTrigger>}
              </TabsList>
              {channels.includes('email') && (
                <TabsContent value="email" className="space-y-3 mt-3">
                  <div>
                    <Label className="text-white">From</Label>
                    <Select value={emailAccountId} onValueChange={setEmailAccountId} disabled={readOnly}>
                      <SelectTrigger className="bg-wt-bg mt-1"><SelectValue placeholder="Choose sender…" /></SelectTrigger>
                      <SelectContent>
                        {emailAccounts.length === 0 ? <div className="px-3 py-2 text-sm text-gray-500">No email accounts. Connect one under Integrations → Email.</div> :
                          emailAccounts.map(a => <SelectItem key={a.id} value={a.id}>{a.email || a.account_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-white">Template (optional — fills subject & body)</Label>
                    <Select value={emailTemplateId} onValueChange={setEmailTemplateId} disabled={readOnly}>
                      <SelectTrigger className="bg-wt-bg mt-1"><SelectValue placeholder="Pick a template or compose inline…" /></SelectTrigger>
                      <SelectContent>
                        {emailTemplates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-white">Subject</Label>
                    <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} disabled={readOnly} className="bg-wt-bg mt-1" placeholder="Your subject (supports {{first_name}})" />
                  </div>
                  <div>
                    <Label className="text-white">Body HTML {emailTemplateId && <span className="text-xs text-gray-500">(leave empty to use template's body)</span>}</Label>
                    <Textarea value={emailBodyHtml} onChange={(e) => setEmailBodyHtml(e.target.value)} disabled={readOnly} rows={8} className="bg-wt-bg mt-1 font-mono text-xs" placeholder="<h1>Hi {{first_name}}</h1>..." />
                  </div>
                </TabsContent>
              )}
              {channels.includes('whatsapp') && (
                <TabsContent value="whatsapp" className="space-y-3 mt-3">
                  <div>
                    <Label className="text-white">Approved Template</Label>
                    <Select value={waTemplateName} onValueChange={setWaTemplateName} disabled={readOnly}>
                      <SelectTrigger className="bg-wt-bg mt-1"><SelectValue placeholder="Choose approved template…" /></SelectTrigger>
                      <SelectContent>
                        {waTemplates.length === 0 ? <div className="px-3 py-2 text-sm text-gray-500">No approved templates. Create one under Marketing → Templates.</div> :
                          waTemplates.map(t => <SelectItem key={t.name} value={t.name}>{t.name} · {t.language}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-white">Language</Label>
                    <Input value={waTemplateLang} onChange={(e) => setWaTemplateLang(e.target.value)} disabled={readOnly} className="bg-wt-bg mt-1 w-32" />
                  </div>
                  <div className="text-xs text-gray-500 flex items-start gap-2 p-2 bg-wt-bg rounded">
                    <Info className="w-4 h-4 shrink-0 mt-0.5" />
                    For templates with body-variable parameters, send from the WhatsApp Integration page for full variable support. This broadcast will send the template without dynamic parameters.
                  </div>
                </TabsContent>
              )}
            </Tabs>
          </section>

          {/* Delivery Rules */}
          <section className="p-4 bg-wt-surface border border-wt-border rounded-lg">
            <div className="font-semibold text-white text-sm mb-3">4. Delivery Rules</div>
            <Label className="text-white">Fallback behavior (applies when multiple channels selected)</Label>
            <Select value={fallbackMode} onValueChange={setFallbackMode} disabled={readOnly}>
              <SelectTrigger className="bg-wt-bg mt-1" data-testid="fallback-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Send on all selected channels (parallel, no fallback)</SelectItem>
                <SelectItem value="on_failure">Try next channel only if previous fails (primary + fallback)</SelectItem>
                <SelectItem value="parallel">Send on all channels regardless of delivery</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-xs text-gray-500 mt-2">
              Channel priority = the order you selected them at the top. Reorder by unchecking + rechecking.
            </div>

            <div className="mt-4 pt-4 border-t border-wt-border">
              <Label className="text-white">Throttle (messages per minute, optional)</Label>
              <Input
                type="number"
                min="0"
                placeholder="Unlimited"
                value={throttlePerMinute}
                onChange={(e) => setThrottlePerMinute(e.target.value)}
                disabled={readOnly}
                data-testid="broadcast-throttle-input"
                className="bg-wt-bg mt-1 w-40"
              />
              <div className="text-xs text-gray-500 mt-1">Leave empty for no throttle. Applies across all channels.</div>
            </div>

            <div className="mt-4 pt-4 border-t border-wt-border">
              <label className="flex items-center gap-2 text-white cursor-pointer">
                <input
                  type="checkbox"
                  checked={sendWindowEnabled}
                  onChange={(e) => setSendWindowEnabled(e.target.checked)}
                  disabled={readOnly}
                  data-testid="broadcast-send-window-toggle"
                />
                <span className="text-sm font-medium">Only send during a specific time window</span>
              </label>
              {sendWindowEnabled && (
                <div className="mt-3 grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-white text-xs">Start hour (0-23)</Label>
                    <Input
                      type="number" min="0" max="23"
                      value={sendWindowStart}
                      onChange={(e) => setSendWindowStart(e.target.value)}
                      disabled={readOnly}
                      data-testid="broadcast-send-window-start"
                      className="bg-wt-bg mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-white text-xs">End hour (0-23)</Label>
                    <Input
                      type="number" min="0" max="23"
                      value={sendWindowEnd}
                      onChange={(e) => setSendWindowEnd(e.target.value)}
                      disabled={readOnly}
                      data-testid="broadcast-send-window-end"
                      className="bg-wt-bg mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-white text-xs">Timezone</Label>
                    <Input
                      value={sendWindowTz}
                      onChange={(e) => setSendWindowTz(e.target.value)}
                      disabled={readOnly}
                      placeholder="UTC, Asia/Kolkata, ..."
                      data-testid="broadcast-send-window-tz"
                      className="bg-wt-bg mt-1"
                    />
                  </div>
                </div>
              )}
              <div className="text-xs text-gray-500 mt-1">
                If launched outside the window, the broadcast auto-reschedules to the next window start.
              </div>
            </div>
          </section>

          {/* Schedule */}
          <section className="p-4 bg-wt-surface border border-wt-border rounded-lg">
            <div className="font-semibold text-white text-sm mb-3">5. Schedule</div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => !readOnly && setScheduleType('now')} className={`px-3 py-2 rounded-lg border transition text-sm ${scheduleType === 'now' ? 'bg-pink-500/20 border-pink-500/50 text-white' : 'bg-wt-bg border-wt-border text-gray-400'}`}>
                <Zap className="w-4 h-4 mr-1 inline" /> Send Now
              </button>
              <button onClick={() => !readOnly && setScheduleType('scheduled')} className={`px-3 py-2 rounded-lg border transition text-sm ${scheduleType === 'scheduled' ? 'bg-pink-500/20 border-pink-500/50 text-white' : 'bg-wt-bg border-wt-border text-gray-400'}`}>
                <Calendar className="w-4 h-4 mr-1 inline" /> Schedule
              </button>
            </div>
            {scheduleType === 'scheduled' && (
              <DateTimeInput
                value={scheduledAt ? format(new Date(scheduledAt), "yyyy-MM-dd'T'HH:mm") : ''}
                onChange={(e) => setScheduledAt(e.target.value ? new Date(e.target.value).toISOString() : null)}
                disabled={readOnly}
                className="bg-wt-bg mt-2 w-64"
              />
            )}
          </section>
        </div>

        {/* Sidebar Summary */}
        <div className="lg:col-span-1">
          <div className="p-4 bg-wt-surface border border-wt-border rounded-lg sticky top-4 space-y-3">
            <div className="text-sm font-semibold text-white">Audience Preview</div>
            {previewing ? (
              <div className="text-sm text-gray-500">Calculating…</div>
            ) : audiencePreview ? (
              <>
                {channels.map(ch => {
                  const count = audiencePreview.per_channel?.[ch] || 0;
                  const Icon = { email: Mail, whatsapp: MessageCircle, sms: MessageSquare }[ch] || Send;
                  return (
                    <div key={ch} className="flex items-center justify-between p-2 bg-wt-bg rounded border border-wt-border">
                      <div className="flex items-center gap-2 text-gray-300 text-sm">
                        <Icon className={`w-4 h-4 ${ch === 'email' ? 'text-amber-400' : ch === 'whatsapp' ? 'text-green-400' : 'text-blue-400'}`} />
                        <span className="capitalize">{ch}</span>
                      </div>
                      <span className="text-white font-semibold">{count.toLocaleString()}</span>
                    </div>
                  );
                })}
                <div className="pt-2 border-t border-wt-border">
                  <div className="text-xs text-gray-500">Total reachable (union)</div>
                  <div className="text-2xl font-bold text-pink-300">{(audiencePreview.total_unique || 0).toLocaleString()}</div>
                </div>
              </>
            ) : (
              <div className="text-sm text-gray-500">Pick audience + channels to preview.</div>
            )}
            <div className="pt-3 border-t border-wt-border text-xs text-gray-500">
              Suppression (unsubscribed + bounced) is applied automatically on email.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BroadcastWizardPage;
